import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { STATUS_LABELS } from "../../components/StatusBadge";
import { Loader2, Euro, PackageCheck, Inbox, Truck, AlertCircle, ReceiptText } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtEur2 = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
};

const FUNNEL_ORDER = ["NEW", "CONFIRMED", "IN_PROGRESS", "DELIVERED", "INVOICED", "CANCELLED", "PENDING_OTP", "DRAFT"];
const FUNNEL_COLORS = {
  NEW: "bg-blue-500",
  CONFIRMED: "bg-yellow-500",
  IN_PROGRESS: "bg-orange-500",
  DELIVERED: "bg-green-600",
  INVOICED: "bg-teal-600",
  CANCELLED: "bg-red-400",
  PENDING_OTP: "bg-purple-400",
  DRAFT: "bg-gray-400",
};

const KpiCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="lc-card p-4">
    <div className="flex items-center gap-2 text-[var(--lc-gray)]">
      <Icon className="w-4 h-4" />
      <span className="lc-small-label">{label}</span>
    </div>
    <div className="text-2xl font-extrabold mt-2">{value}</div>
    {sub && <div className="text-xs text-[var(--lc-gray)] mt-0.5">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div data-testid="dashboard-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[var(--lc-orange)]" /></div>;
  }

  const chartData = data.monthly_revenue.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const funnelMax = Math.max(1, ...FUNNEL_ORDER.map((s) => data.funnel[s] || 0));

  return (
    <div data-testid="admin-dashboard" className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Tableau de bord</h1>
        <p className="text-sm text-[var(--lc-gray)] mt-1">Vos prix de vente réels (hors marge client)</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard testid="kpi-revenue-month" icon={Euro} label="CA du mois (HT)" value={fmtEur2(data.current_month.total_ht)} sub={`${fmtEur2(data.current_month.total_ttc)} TTC`} />
        <KpiCard testid="kpi-delivered-month" icon={PackageCheck} label="Livrées ce mois" value={data.current_month.delivered} />
        <KpiCard testid="kpi-to-invoice" icon={ReceiptText} label="À facturer" value={data.to_invoice ?? data.funnel.DELIVERED ?? 0} sub="Livrées non facturées" />
        <KpiCard testid="kpi-pending-requests" icon={Inbox} label="Demandes à traiter" value={data.pending_requests} />
        <KpiCard testid="kpi-upcoming" icon={Truck} label="Livraisons à venir" value={data.upcoming_deliveries} sub="Confirmées + en cours" />
      </div>

      <div className="lc-card p-4 sm:p-6">
        <div className="lc-small-label mb-4">Chiffre d'affaires mensuel (HT, commandes livrées)</div>
        <div data-testid="revenue-chart" className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtEur(v)} />
              <Tooltip
                formatter={(v, name) => [fmtEur2(v), name === "total_ht" ? "CA HT" : name]}
                labelFormatter={(l) => `Mois : ${l}`}
              />
              <Bar dataKey="total_ht" fill="var(--lc-orange, #f97316)" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="lc-card p-4 sm:p-6">
        <div className="lc-small-label mb-4">Entonnoir des commandes par statut</div>
        <div className="space-y-3">
          {FUNNEL_ORDER.map((s) => {
            const count = data.funnel[s] || 0;
            return (
              <div key={s} data-testid={`funnel-${s.toLowerCase()}`} className="flex items-center gap-3">
                <div className="w-36 sm:w-44 text-xs font-semibold shrink-0">{STATUS_LABELS[s]}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                  <div
                    className={`${FUNNEL_COLORS[s]} h-full rounded-full transition-all`}
                    style={{ width: `${Math.max(count > 0 ? 4 : 0, (count / funnelMax) * 100)}%` }}
                  />
                </div>
                <div className="w-8 text-right text-sm font-bold shrink-0">{count}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
